import { useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { CtaLink } from '@/components/bedrock';
import { CHROME_CTAS } from '@/components/chrome';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { useT } from '@/core/I18nProvider';
import { appLoginUrl } from '@/lib/site-url';

import { isNavLinkActive } from './nav-active';

import type { ResolvedChromeNavLink } from '@/components/chrome';
import type { ReactElement } from 'react';

/**
 * The narrow-viewport nav — a disclosure button in the bar, and a panel that
 * drops beneath it.
 *
 * ## Why this exists at all, given the "no hamburger" constraint it replaces
 *
 * The header used to keep every link visible by moving them to a SECOND ROW: a
 * horizontally-scrolling strip under the bar at anything below 1280px. The
 * constraint behind it was reasonable — a menu costs a tap, and a link nobody
 * can see is a link nobody clicks — but the result was a two-storey header on
 * every laptop and every phone, with a strip that clipped mid-word at its right
 * edge and had no affordance saying it could scroll. It read as a layout that
 * had broken rather than as a design.
 *
 * The bar is one row now, at every width, and the links it cannot fit go behind
 * one control that says how many there are by opening onto all of them at once.
 * That is the trade the old comment declined to make; the strip is what made it
 * worth making.
 *
 * ## The panel is a disclosure, not a menu widget
 *
 * Deliberately: its contents are ordinary links to ordinary pages, and the APG
 * `menu` pattern would replace the browser's own link behaviour (open in a new
 * tab, copy address, arrow-key-free tab order) with a roving tabindex that
 * gains nothing here. So the button carries `aria-expanded` + `aria-controls`
 * over a plain `<nav>`, and the links stay links.
 *
 * What it DOES take from the menu pattern is the three dismissals a keyboard or
 * pointer user expects — Escape, an outside click, and a route change — plus
 * returning focus to the trigger on Escape, so a keyboard user is never dropped
 * at the top of the document.
 *
 * ## Scroll is locked while it is open
 *
 * The panel is `fixed` under a `sticky` header, so without this the page scrolls
 * beneath an overlay that stays put — which reads as the page having come apart.
 */
export function NavMenu({
  links,
}: {
  readonly links: readonly ResolvedChromeNavLink[];
}): ReactElement {
  const { t } = useT();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const panelId = `nav-menu-${useId().replace(/:/g, '')}`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // A route change closes the panel. Tapping a link inside it navigates, and a
  // panel still covering the page you just asked for is the commonest bug in
  // this component's shape — so it is handled by the ROUTE rather than by an
  // onClick on each link, which would miss a browser Back.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return undefined;

    function onKey(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    }
    function onPointer(e: MouseEvent): void {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    }

    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);

    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
      body.style.overflow = previousOverflow;
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={open ? t('header.closeMenu') : t('header.openMenu')}
        onClick={() => {
          setOpen((v) => !v);
        }}
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-[color:var(--bd-ink)] transition-colors hover:bg-[color:var(--bd-sunk)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--bd-blue)] focus-visible:ring-offset-2 lg:hidden"
      >
        {/* Two bars that become an X. `aria-hidden` because the button's own
            label already says which state it is in — announcing the glyph too
            would say it twice. */}
        <span aria-hidden="true" className="relative block h-4 w-5">
          <span
            className={`absolute inset-x-0 block h-[2px] rounded-full bg-current transition-transform duration-200 ${
              open ? 'top-1/2 -translate-y-1/2 rotate-45' : 'top-[2px]'
            }`}
          />
          <span
            className={`absolute inset-x-0 block h-[2px] rounded-full bg-current transition-transform duration-200 ${
              open ? 'top-1/2 -translate-y-1/2 -rotate-45' : 'bottom-[2px]'
            }`}
          />
        </span>
      </button>

      {open ? (
        <div
          id={panelId}
          ref={panelRef}
          className="fixed inset-x-0 top-[var(--bd-nav-h)] z-40 max-h-[calc(100dvh-var(--bd-nav-h))] overflow-y-auto border-b border-[color:var(--bd-line)] bg-[color:var(--bd-page)] shadow-[0_18px_40px_-24px_rgb(15_23_42/0.35)] lg:hidden"
        >
          <nav aria-label={t('header.menu')} className="mx-auto w-full max-w-[var(--bd-maxw)] p-4">
            <ul className="flex flex-col gap-0.5">
              {links.map((link) => {
                const active = isNavLinkActive(pathname, link.href);
                return (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      {...(active ? { 'aria-current': 'page' as const } : {})}
                      className={`flex min-h-[44px] items-center rounded-lg px-3 text-[0.98rem] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--bd-blue)] ${
                        active
                          ? 'bg-[color:var(--bd-sunk)] font-medium text-[color:var(--bd-ink)]'
                          : 'text-[color:var(--bd-body)] hover:bg-[color:var(--bd-sunk)] hover:text-[color:var(--bd-ink)]'
                      }`}
                    >
                      {t(link.labelKey)}
                    </Link>
                  </li>
                );
              })}
            </ul>

            {/* Sign-in rides in the panel rather than in the bar below `lg`.
                The bar keeps the brand, the menu and the ONE primary action;
                a second CTA beside them is what used to push the German and
                Hebrew rows past the viewport edge. */}
            <div className="mt-3 border-t border-[color:var(--bd-line)] pt-3">
              <CtaLink href={appLoginUrl()} variant="secondary" size="sm" className="w-full">
                {t(CHROME_CTAS.secondary.labelKey.site)}
              </CtaLink>
            </div>
          </nav>
        </div>
      ) : null}
    </>
  );
}
